import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderWidthProps,
  BoxShadowProps,
  DisplayProps,
  JustifyContentProps,
  OpacityProps,
  PositionProps,
  TopProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderWidth,
  boxShadow,
  display,
  justifyContent,
  opacity,
  position,
  space,
  themeGet,
  top,
  zIndex,
} from 'styled-system';

import type { BackgroundColorProps, SpaceProps } from '../../../../theme/system-types';
import type {
  CursorProps,
  ThemeLeftProps,
  ThemeRightProps,
  TransformProps,
  TransitionProps,
} from '../../../../theme/system-utilities';
import {
  cursor,
  themeLeft,
  themeRight,
  transform,
  transition,
} from '../../../../theme/system-utilities';
import Icon from '../../../Icon';

const ICON_SIZE = '32px';

export const NavigationButton = styled.button<
  PositionProps &
    SpaceProps &
    AlignItemsProps &
    JustifyContentProps &
    ZIndexProps &
    BoxShadowProps &
    BackgroundColorProps &
    TopProps &
    BorderRadiusProps &
    BorderProps &
    DisplayProps &
    TransitionProps &
    OpacityProps &
    CursorProps &
    ThemeLeftProps &
    ThemeRightProps &
    TransformProps &
    BorderWidthProps &
    BorderColorProps
>`
  ${alignItems};
  ${backgroundColor};
  ${borderColor};
  ${borderRadius};
  ${borderWidth};
  ${border};
  ${boxShadow};
  ${cursor};
  ${display};
  ${justifyContent};
  ${opacity};
  ${position};
  ${space};
  ${themeLeft}
  ${themeRight};
  ${top};
  ${transform};
  ${transition};
  ${zIndex};

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.default')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: ${themeGet('colors.sys.neutral.border.weak')};
  }
`;

const navigationButtonStyles: ComponentProps<typeof NavigationButton> = {
  alignItems: 'center',
  bg: 'sys.neutral.background.default',
  borderColor: 'sys.neutral.border.default',
  borderRadius: 'circle',
  borderWidth: '1px',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  opacity: 0.8,
  padding: 'sp_8',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  transition: 'background-color 200ms ease',
  zIndex: 1,
  tabIndex: -1,
};

const previousButtonStyles: ComponentProps<typeof NavigationButton> = {
  ...navigationButtonStyles,
  themeLeft: '-60px',
};

const nextButtonStyles: ComponentProps<typeof NavigationButton> = {
  ...navigationButtonStyles,
  themeRight: '-60px',
};

export const NextCarouselButton = (props: ComponentProps<typeof NavigationButton>) => (
  <NavigationButton aria-hidden={true} {...nextButtonStyles} {...props}>
    <Icon name="arrowRight" size={ICON_SIZE} fill="sys.neutral.icon.default" />
  </NavigationButton>
);

export const PrevCarouselButton = (props: ComponentProps<typeof NavigationButton>) => (
  <NavigationButton aria-hidden={true} {...previousButtonStyles} {...props}>
    <Icon name="arrowLeft" size={ICON_SIZE} fill="sys.neutral.icon.default" />
  </NavigationButton>
);
